import { useCallback, useEffect, useRef } from "react";
import {
  useBlocker,
  type Blocker,
  type BlockerFunction,
  type Location,
} from "react-router-dom";

// Screens with unsaved changes right now: a new build waits for them instead of reloading (build-freshness.ts).
const dirtyScreens = new Set<object>();
export function hasUnsavedChanges(): boolean {
  return dirtyScreens.size > 0;
}

const DEFAULT_MESSAGE = "Есть неопубликованные изменения. Покинуть экран и потерять их?";
const NO_IGNORED_SEARCH_PARAMS: readonly string[] = [];

type NavigationLocation = Pick<Location, "pathname" | "search" | "hash">;

export function shouldBlockUnsavedNavigation(
  dirty: boolean,
  currentLocation: NavigationLocation,
  nextLocation: NavigationLocation,
  ignoredSearchParams: readonly string[] = [],
): boolean {
  const searchChanged = ignoredSearchParams.length === 0
    ? currentLocation.search !== nextLocation.search
    : (() => {
        const current = new URLSearchParams(currentLocation.search);
        const next = new URLSearchParams(nextLocation.search);
        for (const key of ignoredSearchParams) {
          current.delete(key);
          next.delete(key);
        }
        current.sort();
        next.sort();
        return current.toString() !== next.toString();
      })();

  return dirty && (
    currentLocation.pathname !== nextLocation.pathname
    || searchChanged
    || currentLocation.hash !== nextLocation.hash
  );
}

export function resolveBlockedNavigation(
  blocker: Blocker,
  message: string,
  confirmDiscard: (message: string) => boolean,
): void {
  if (blocker.state !== "blocked") return;
  if (confirmDiscard(message)) {
    blocker.proceed();
  } else {
    blocker.reset();
  }
}

export function useUnsavedChangesGuard(
  dirty: boolean,
  message = DEFAULT_MESSAGE,
  ignoredSearchParams: readonly string[] = NO_IGNORED_SEARCH_PARAMS,
): () => boolean {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => shouldBlockUnsavedNavigation(
      dirtyRef.current,
      currentLocation,
      nextLocation,
      ignoredSearchParams,
    ),
    [ignoredSearchParams],
  );
  const blocker = useBlocker(shouldBlock);
  const screen = useRef({}).current;

  useEffect(() => {
    if (!dirty) return;
    dirtyScreens.add(screen);
    return () => { dirtyScreens.delete(screen); };
  }, [dirty, screen]);

  useEffect(() => {
    function beforeUnload(event: BeforeUnloadEvent) {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, []);

  useEffect(() => {
    resolveBlockedNavigation(blocker, message, (prompt) => window.confirm(prompt));
  }, [blocker, message]);

  return useCallback(() => !dirtyRef.current || window.confirm(message), [message]);
}
